import React, { useState, useMemo } from 'react';
import SummaryTriad from '../diagnostic/SummaryTriad';
import KnowledgePanel from '../diagnostic/KnowledgePanel';
import BehaviourInsights from '../diagnostic/BehaviourInsights';
import TopicMasteryList from '../diagnostic/TopicMasteryList';
import StrengthsImprovements from '../diagnostic/StrengthsImprovements';
import RecommendationList from '../diagnostic/RecommendationList';
import '../diagnostic/DiagnosticReportPro.css';
import { num, metricsFromRow, attemptLabel, fmtDate } from './aptitudeHelpers';

// Feature 2 — View any historical aptitude attempt exactly as it was generated.
// The full report is reconstructed purely from the STORED row (knowledge,
// topic_analysis, strengths, weaknesses, ai_recommendations, scalar metrics).
// Nothing is recalculated — this is the frozen snapshot from that submission.

const Sec = ({ title, sub, children }) => (
  <section className="dxr-section">
    {title && <div className="dxr-section-head"><h2 className="dxr-section-title">{title}</h2>{sub && <p className="dxr-section-desc">{sub}</p>}</div>}
    {children}
  </section>
);

const AptitudeHistoryViewer = ({ history }) => {
  const rows = useMemo(
    () => [...(history || [])].sort((a, b) => (b.attempt_number || 0) - (a.attempt_number || 0)),
    [history]
  );
  const [sel, setSel] = useState(rows[0]?.attempt_id || null);

  if (rows.length === 0) {
    return (
      <div className="apt-empty">
        <span className="i">🗂️</span>
        <h3 style={{ margin: '0 0 6px', color: '#0f172a' }}>No attempts to browse yet</h3>
        <p style={{ margin: 0 }}>Once aptitude tests are completed, each stored report can be reopened here.</p>
      </div>
    );
  }

  const row = rows.find((r) => r.attempt_id === sel) || rows[0];
  const metrics = metricsFromRow(row);
  const knowledge = row.knowledge || (num(row.overall_score) != null ? { score: num(row.overall_score) } : null);
  const topics = row.topic_analysis || [];

  return (
    <>
      {/* Attempt selector strip */}
      <div className="apt-attempt-strip">
        {rows.map((r) => (
          <button key={r.attempt_id} className={`apt-attempt-btn ${r.attempt_id === row.attempt_id ? 'active' : ''}`} onClick={() => setSel(r.attempt_id)}>
            <div className="no">{attemptLabel(r)}</div>
            <div className="er">{num(r.eri_score) == null ? '—' : `${Math.round(num(r.eri_score))}%`}</div>
            <div className="dt">{fmtDate(r.submitted_at)}</div>
          </button>
        ))}
      </div>

      <div className="apt-snapshot-banner">
        <span className="badge">Snapshot</span>
        <span className="txt">{attemptLabel(row)} · {row.test_name || 'Aptitude Test'} — {fmtDate(row.submitted_at)}</span>
        <span className="note">Stored analytics, exactly as generated after submission</span>
      </div>

      {/* Full stored report */}
      <Sec>
        <SummaryTriad
          knowledgeScore={num(row.overall_score)}
          behaviourScore={num(row.behaviour_score)}
          eriScore={num(row.eri_score)}
        />
      </Sec>

      {knowledge && (
        <Sec title="Knowledge Performance" sub="Academic performance recorded for this attempt">
          <KnowledgePanel knowledge={knowledge} />
        </Sec>
      )}

      {metrics && (
        <Sec title="Behaviour Insights" sub="How this attempt was worked through">
          <BehaviourInsights metrics={metrics} />
        </Sec>
      )}

      {topics.length > 0 && (
        <Sec title="Topic Mastery" sub="Accuracy by topic for this attempt">
          <TopicMasteryList topics={topics} />
        </Sec>
      )}

      <Sec title="Strengths & Improvement Areas">
        <StrengthsImprovements strengths={row.strengths} metrics={metrics} topics={topics} />
      </Sec>

      {row.ai_recommendations?.length > 0 && (
        <Sec title="Personalized Learning Recommendations">
          <RecommendationList recommendations={row.ai_recommendations} />
        </Sec>
      )}
    </>
  );
};

export default AptitudeHistoryViewer;
